import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthShell from "../../components/layout/AuthShell";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";
import { formatApiError } from "../../context/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError("Passwords do not match.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Reset Password" subtitle="This link is invalid.">
        <p className="text-muted-foreground">The reset link is missing or invalid. <Link to="/forgot-password" className="text-gold hover:underline">Request a new one</Link>.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set New Password" subtitle="Choose a strong new password." footer={<p><Link to="/login" className="text-gold hover:underline">Back to sign in</Link></p>}>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="space-y-2">
          <Label htmlFor="password" className="text-white/80">New password</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="reset-password-input" className="bg-navy border-white/15 text-white h-11 focus-visible:ring-gold" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm" className="text-white/80">Confirm password</Label>
          <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" data-testid="reset-confirm-input" className="bg-navy border-white/15 text-white h-11 focus-visible:ring-gold" />
        </div>
        {error && <p className="text-sm text-firered" data-testid="reset-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full cf-gold-btn font-semibold h-11" data-testid="reset-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
        </Button>
      </form>
    </AuthShell>
  );
}
